import React, { useEffect, useState } from 'react';
import { formatLongDate } from '../../../utils/dateFormat';
import { getLeaveRequests, cancelLeaveRequest, postLeaveRequest } from './api';
import { toast } from 'react-toastify';
import ApplyLeaveModal from './ApplyLeaveModal';
import { fetchCollection } from '../../../api/common';
import { safeApiCall } from '../../../utils/apiHelpers';

const statusColors = {
  Approved: 'text-green-600',
  Pending: 'text-yellow-700',
  Rejected: 'text-red-600',
  Cancelled: 'text-gray-400',
};

const LeaveRequest = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applyOpen, setApplyOpen] = useState(false);
  const [collection, setCollection] = useState(null);
  const [collectionLoading, setCollectionLoading] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const data = await safeApiCall(getLeaveRequests);
      setRequests(data);
    } catch (e) {
      toast.error('Failed to fetch leave requests');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (id) => {
    try {
      await safeApiCall(cancelLeaveRequest, id);
      toast.success('Leave request cancelled');
      fetchRequests();
    } catch (e) {
      toast.error('Failed to cancel leave request');
    }
  };

  const handleApplyClick = async () => {
    setCollectionLoading(true);
    try {
      const data = await safeApiCall(fetchCollection, ['leave_types', 'leave_day_type', 'holidays']);
      setCollection(data);
      setApplyOpen(true);
    } catch (e) {
      toast.error('Failed to load leave types');
    } finally {
      setCollectionLoading(false);
    }
  };

  const handleApplyLeave = async (payload) => {
    setCollectionLoading(true);
    try {
      const data = await safeApiCall(postLeaveRequest, payload);

      if (data) {
        toast.success('Leave request submitted');
        setApplyOpen(false);
        fetchRequests();
      }
    } catch (e) {
      toast.error('Failed to apply for leave');
    } finally {
      setCollectionLoading(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-2xl font-bold">Leave Requests</h2>
        <button
          className="bg-blue-600 text-white px-4 py-2 rounded shadow hover:bg-blue-700"
          onClick={handleApplyClick}
          disabled={collectionLoading}
        >
          Apply Leave
        </button>
      </div>
      <div className="flex items-center gap-4 mb-2">
        <select className="border rounded px-2 py-1 text-sm" defaultValue="All Requests" disabled>
          <option>All Requests</option>
        </select>
        <span className="text-sm">Total Records: {requests.length}</span>
        <select className="border rounded px-2 py-1 text-sm" defaultValue={20} disabled>
          <option value={20}>20</option>
        </select>
        <span className="text-sm">Records per page</span>
      </div>
      <div className="bg-white rounded shadow overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-slate-100">
              <th className="px-4 py-2 border">Status</th>
              <th className="px-4 py-2 border">Employee Name</th>
              <th className="px-4 py-2 border">Leave Type</th>
              <th className="px-4 py-2 border">Reason for Leave</th>
              <th className="px-4 py-2 border">No of Days</th>
              <th className="px-4 py-2 border"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center py-6">Loading...</td></tr>
            ) : requests.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-6">No leave requests found.</td></tr>
            ) : (
              requests.map(req => {
                const days = req.dates.length;
                const start = req.dates[0]?.date;
                const end = req.dates[days-1]?.date;
                return (
                  <tr key={req.id}>
                    <td className={`px-4 py-2 border font-semibold flex items-center gap-1 ${statusColors[req.status_of_leave] || ''}`}>
                      {req.status_of_leave === 'Approved' && <span>✔</span>}
                      {req.status_of_leave === 'Pending' && <span>⏳</span>}
                      {req.status_of_leave === 'Rejected' && <span>✖</span>}
                      {req.status_of_leave === 'Cancelled' && <span>🚫</span>}
                      {req.status_of_leave}
                    </td>
                    <td className="px-4 py-2 border">{req.employee_full_name}</td>
                    <td className="px-4 py-2 border">{req.leave_type_name}</td>
                    <td className="px-4 py-2 border whitespace-pre-line">{req.reason_for_leave}</td>
                    <td className="px-4 py-2 border text-center">
                      {req.dates.map((d, idx) => (
                        <div key={d.id || idx}>
                          {formatLongDate(d.date)}<br/>
                          <span className="font-bold">{d.leave_day_type}</span>
                        </div>
                      ))}
                    </td>
                    <td className="px-4 py-2 border text-center">
                      {req.status_of_leave === 'Pending' && (
                        <button className="bg-red-500 text-white px-3 py-1 rounded text-xs" onClick={() => handleCancel(req.id)}>
                          Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {/* Pagination: only page 1 for now */}
      <div className="flex justify-center mt-4">
        <button className="px-3 py-1 border rounded bg-blue-100 text-blue-700" disabled>1</button>
      </div>
      {/* Apply Leave Modal */}
      {applyOpen && collection && (
        <ApplyLeaveModal
          open={applyOpen}
          onClose={() => setApplyOpen(false)}
          onSubmit={handleApplyLeave}
          collection={collection}
          reportingManagerId={null}
        />
      )}
    </div>
  );
};

export default LeaveRequest;
